import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      // Only redirect if not already on auth pages
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        // Save current path so LoginPage can redirect back after login
        localStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Countries API
export const countriesApi = {
  getAll: () => api.get('/countries'),
  getPopular: () => api.get('/countries/popular'),
  getById: (id) => api.get(`/countries/${id}`),
  getBySlug: (slug) => api.get(`/countries/by-slug/${slug}`),
  getVisaTypes: (countryId) => api.get(`/countries/${countryId}/visa-types`),
  getRequirements: (countryId) => api.get(`/countries/${countryId}/requirements`),
  create: (data) => api.post('/countries', data),
  update: (id, data) => api.put(`/countries/${id}`, data),
  delete: (id) => api.delete(`/countries/${id}`)
};

// Visa Types API
export const visaTypesApi = {
  getAll: () => api.get('/visa-types'),
  getPopular: () => api.get('/visa-types/popular'),
  getById: (id) => api.get(`/visa-types/${id}`),
  create: (data) => api.post('/visa-types', data),
  update: (id, data) => api.put(`/visa-types/${id}`, data),
  delete: (id) => api.delete(`/visa-types/${id}`)
};

// Eligibility API
export const eligibilityApi = {
  check: (passportCountry, destinationCountry) => 
    api.post('/eligibility-check', { passport_country: passportCountry, destination_country: destinationCountry })
};

// Applications API
export const applicationsApi = {
  getMyApplications: () => api.get('/applications'),
  getById: (id) => api.get(`/applications/${id}`),
  create: (data) => api.post('/applications', data),
  saveDraft: (data) => api.post('/applications/draft', data),
  update: (id, data) => api.put(`/applications/${id}`, data),
  submit: (id) => api.post(`/applications/${id}/submit`),
  delete: (id) => api.delete(`/applications/${id}`),
  getActivityLog: (id) => api.get(`/applications/${id}/activity-log`),
  addComment: (id, message) => api.post(`/applications/${id}/comment`, { message })
};

// Documents API
export const documentsApi = {
  upload: (file, applicationId, travelerIndex, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/upload?application_id=${applicationId}&traveler_index=${travelerIndex}&document_type=${documentType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getById: (id) => api.get(`/documents/${id}`),
  passportScan: (file, side = 'front') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/passport-scan?side=${side}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  verify: (documentId, isVerified, notes) => 
    api.post(`/documents/${documentId}/verify`, { document_id: documentId, is_verified: isVerified, verification_notes: notes }),
  autoCheck: (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    return api.post('/documents/auto-check', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  reupload: (documentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/${documentId}/reupload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Notifications API
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: (notificationId) => api.post(`/notifications/${notificationId}/read`),
  markUnread: (notificationId) => api.post(`/notifications/${notificationId}/unread`),
  markAllRead: () => api.post('/notifications/read-all')
};

// Payments API
export const paymentsApi = {
  createOrder: (applicationId, amount, currency = 'INR') => 
    api.post('/payments/create-order', { application_id: applicationId, amount, currency }),
  verify: (orderId, paymentId, signature) => 
    api.post('/payments/verify', { order_id: orderId, razorpay_payment_id: paymentId, razorpay_signature: signature }),
  getStatus: (applicationId) => api.get(`/payments/${applicationId}/status`)
};

// Admin API
export const adminApi = {
  getDashboard: () => api.get('/admin/dashboard'),
  getApplications: (params) => api.get('/admin/applications', { params }),
  getUsers: (params) => api.get('/admin/users', { params }),
  createEmployee: (data) => api.post('/admin/employees', data),
  updateEmployee: (id, data) => api.put(`/admin/employees/${id}`, data),
  deleteEmployee: (id) => api.delete(`/admin/employees/${id}`)
};

// Employee API
export const employeeApi = {
  getDashboard: () => api.get('/employee/dashboard'),
  getApplications: (params) => api.get('/employee/applications', { params }),
  assignApplication: (applicationId) => api.post(`/employee/applications/${applicationId}/assign`)
};

// B2B Partner API
export const b2bApi = {
  register: (data) => api.post('/b2b/register', data),
  getDashboard: () => api.get('/b2b/dashboard'),
  getApplications: () => api.get('/b2b/applications'),
  createApplication: (data) => api.post('/b2b/applications', data),
  getProfile: () => api.get('/b2b/profile'),
};

// OTP API
export const otpApi = {
  send: (phone, applicationId) => api.post('/otp/send', { phone, application_id: applicationId }),
  verify: (otp, applicationId) => api.post('/otp/verify', { otp, application_id: applicationId }),
  status: (applicationId) => api.get(`/otp/status/${applicationId}`),
};


// get contact messages for admin
export const contactApi = {
  getAll: () => api.get('/admin/contacts'),
};


export default api;
